import type { ComponentType } from 'react';
import type { useGT } from 'gt-react';
import type { ReportDataset } from './ReportDataContext';
import type { BaseComponentConfig } from './types';

/** What a component type's default title reads. */
export interface DefaultTitleContext {
  /** The dataset the component charts; null before it exists. */
  dataset: ReportDataset | null;
  gt: ReturnType<typeof useGT>;
}

// ── Component type registry ───────────────────────────────────────────────────
// Everything the canvas needs to know about a component type lives in one
// definition: default-config factory, renderer, config form, palette preview.
// The five builtin charts register here (see builtinCharts.ts); surfaces can
// extend the registry with their own widget types (e.g. AR-specific cards)
// without the canvas ever importing feature code.

export interface ComponentTypeDefinition<
  C extends BaseComponentConfig = BaseComponentConfig,
> {
  type: string;
  /** Shown in the palette, drag preview, and config popover header. */
  label: string;
  /**
   * Machine-facing hint for AI layout generation: what the widget shows,
   * config guidance (e.g. enum values), and whether it binds a dataset via
   * `datasetKey` or is self-contained. Never rendered to users.
   */
  description?: string;
  /**
   * Config fields whose value must name a column of the bound dataset (e.g.
   * `['xAxisKey', 'yAxisKey']`). AI-layout generation validates these refs
   * against the dataset and asks for a repair when they don't resolve. Omit
   * for components that reference no columns (self-contained widgets, or the
   * vega-chart whose refs live inside its opaque spec).
   */
  columnRefFields?: readonly string[];
  /**
   * Current config schema version (start at 1). Adding an optional field with
   * a default needs no bump (hydration merges onto `make()` defaults);
   * renames/semantic changes bump this and add a migration step.
   */
  version: number;
  /**
   * Step migrations: migrations[n] upgrades a persisted config from version n
   * to n+1. Steps are pure and kept forever — persisted layouts migrate lazily
   * on read.
   */
  migrations?: Record<
    number,
    (config: Record<string, unknown>) => Record<string, unknown>
  >;
  /**
   * The title shown above the component while its config sets none (e.g.
   * "Net by Customer" from its columns). Absent = the component shows a title
   * only when its config sets one — for self-titled widgets such as metric
   * tiles.
   */
  defaultTitle?: (component: C, context: DefaultTitleContext) => string;
  /**
   * The renderer draws its own title (with its description, help icon, …),
   * so the canvas doesn't print one above it.
   */
  rendersOwnTitle?: boolean;
  /**
   * How big a fresh component is on the Report grid, and the smallest it may
   * be resized to, in grid cells. Absent = {@link DEFAULT_COMPONENT_SIZE}.
   */
  size?: ComponentSize;
  /** Default config for a freshly dropped component. */
  make: (id: string) => C;
  Renderer: ComponentType<{ component: C }>;
  /**
   * The config form carries its own AI prompt (the vega chart generates its
   * spec from a description), so the sidebar offers no second "Edit with AI".
   */
  generatesInConfigForm?: boolean;
  /** null = component has no configuration; the gear button is hidden. */
  ConfigForm: ComponentType<{
    component: C;
    onUpdate: (updated: C) => void;
    /** Closes the popover the form is in, when it is in one. */
    onClose?: () => void;
  }> | null;
  /** Palette thumbnail. */
  Preview: ComponentType;
}

export type ComponentRegistry = Record<string, ComponentTypeDefinition>;

/** A component type's size on the Report grid, in grid cells. */
export interface ComponentSize {
  /** Columns a fresh component spans. */
  w: number;
  /** Grid rows a fresh component is tall. */
  h: number;
  minW: number;
  minH: number;
}

/** A chart's size: half the grid wide, 280px tall. */
export const DEFAULT_COMPONENT_SIZE: ComponentSize = {
  w: 6,
  h: 6,
  minW: 3,
  minH: 3,
};

/** The size a component of `type` takes; the default for a type the registry lacks. */
export function componentSize(
  registry: ComponentRegistry,
  type: string,
): ComponentSize {
  return registry[type]?.size ?? DEFAULT_COMPONENT_SIZE;
}

/**
 * Widens a concretely-typed definition to the registry's base-typed entry.
 * This is the single sanctioned variance cast in the canvas: a component of
 * type `t` is only ever rendered/configured by the entry registered under `t`,
 * so the erased generic is safe at runtime.
 */
export function defineComponentType<C extends BaseComponentConfig>(
  def: ComponentTypeDefinition<C>,
): ComponentTypeDefinition {
  return def as unknown as ComponentTypeDefinition;
}
